<script>
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { ref } from "vue"
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/vue/20/solid"
import { useI18n } from "vue-i18n"

export default {
  name: "Header",
  components: {
    ChevronDownIcon,
    ChevronUpIcon,
  },
  props: {
    dark: Boolean,
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const { t } = useI18n()

    const useCases = ref([
      {
        name: t("installer.header.use-cases.artisan.name"),
        description: t("installer.header.use-cases.artisan.description"),
        slug: "artisan",
      },
      {
        name: t("installer.header.use-cases.pme-locale.name"),
        description: t("installer.header.use-cases.pme-locale.description"),
        slug: "regional",
      },
      {
        name: t("installer.header.use-cases.national.name"),
        description: t("installer.header.use-cases.national.description"),
        slug: "national",
      },
      {
        name: t("installer.header.use-cases.plug-and-play.name"),
        description: t("installer.header.use-cases.plug-and-play.description"),
        slug: "plug-and-play",
      },
      {
        name: t("installer.header.use-cases.franchise.name"),
        description: t("installer.header.use-cases.franchise.description"),
        slug: "franchise",
      },
      {
        name: t("installer.header.use-cases.developer.name"),
        description: t("installer.header.use-cases.developer.description"),
        slug: "developer",
      },
    ])
    const showDropdown = ref(true)
    const selectedProduct = ref(null)
    const selectedCases = ref(null)
    const isHovered = ref(false)

    const products = ref([
      {
        "product-name": t("installer.header.products.sizing.product-name"),
        description: t("installer.header.products.sizing.description"),
        to: { name: "InstallerDesign" },
      },
      {
        "product-name": t("installer.header.products.studies.product-name"),
        description: t("installer.header.products.studies.description"),
        to: { name: "InstallerDigitalSalesTools" },
      },
      {
        "product-name": t("installer.header.products.qtv.product-name"),
        description: t("installer.header.products.qtv.description"),
        to: { name: "InstallerHome" },
      },
      {
        "product-name": t("installer.header.products.procedures.product-name"),
        description: t("installer.header.products.procedures.description"),
        to: { name: "InstallerAdministration" },
      },
      {
        "product-name": t("installer.header.products.stock.product-name"),
        description: t("installer.header.products.stock.description"),
        to: { name: "HardwareMarketplaceAbout" },
      },
      {
        "product-name": t("installer.header.products.management.product-name"),
        description: t("installer.header.products.management.description"),
        to: { name: "InstallerHome" },
      },
    ])
    return {
      route,
      useCases,
      products,
      showDropdown,
      selectedProduct,
      selectedCases,
      isHovered,
    }
  },
  data: function () {
    return {
      mobileNavOpen: false,
      top: true,
      showInstallers: false,
    }
  },
  mounted() {
    document.addEventListener("click", this.clickOutside)
    document.addEventListener("keydown", this.keyPress)
    document.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    document.removeEventListener("click", this.clickOutside)
    document.removeEventListener("keydown", this.keyPress)
    document.removeEventListener("scroll", this.handleScroll)
  },
  methods: {
    clickOutside(e) {
      if (
        !this.mobileNavOpen ||
        this.$refs.mobileNav.contains(e.target) ||
        this.$refs.hamburger.contains(e.target)
      )
        return
      this.mobileNavOpen = false
    },
    keyPress() {
      if (!this.mobileNavOpen || event.keyCode !== 27) return
      this.mobileNavOpen = false
    },
    handleScroll() {
      this.top = window.pageYOffset > 10 ? false : true
    },
    selectProduct(product) {
      this.selectedProduct = product
      this.$router.push(product.to)
      this.showDropdown = false
      setTimeout(() => {
        this.showDropdown = true
      }, 1000)
    },
    selectCases(useCase) {
      this.selectedCases = useCase
      this.$router.push({
        name: "InstallerUseCase",
        params: { slug: useCase.slug },
      })
      this.showDropdown = false
      setTimeout(() => {
        this.showDropdown = true
      }, 1000)
    },
    onHandleHover(event) {
      if (event == "enter") {
        this.isHovered = true
      } else {
        this.isHovered = false
      }
    },
  },
}
</script>

<template>
  <div class="relative">
    <header
      class="fixed w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out"
      :class="{
        'bg-white backdrop-blur-sm shadow-lg': !top,
        'md:!bg-opacity-100': isHovered,
      }"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6">
        <div class="flex items-center justify-between h-16 md:h-20">
          <!-- Site branding -->
          <div class="shrink-0 mr-4">
            <!-- Logo -->
            <router-link
              :to="{ name: 'InstallerHome' }"
              class="flex grow justify-end flex-wrap items-center gap-2"
              aria-label="projetsolaire"
            >
              <img
                class="w-10 h-10 md:w-12 md:h-12"
                src="https://images.ctfassets.net/1b6f0wuf6ave/42P3RP32nltWjyhI0fg0PY/57029d3dbc531bad21cd75c871078436/logo_official_1.svg"
              />
              <h2
                :class="dark && top ? 'text-gray-200' : 'text-gray-800'"
                class="hidden md:block h2 text-lg md:text-xl"
              >
                {{ $t("navigation.installers") }}
              </h2>
            </router-link>
          </div>

          <!-- Desktop navigation -->
          <nav class="hidden md:flex md:grow">
            <ul class="flex grow flex-wrap items-center justify-between mt-1">
              <div class="flex items-center gap-6 ml-12">
                <li>
                  <div
                    class="flex items-center gap-2 group cursor-pointer"
                    @mouseover="onHandleHover('enter')"
                    @mouseleave="onHandleHover('leave')"
                  >
                    <div
                      :class="
                        dark && top
                          ? 'text-[#666666] group-hover:text-[#666666]'
                          : 'text-[#666666] group-hover:text-[#666666]'
                      "
                      class="flex items-center transition duration-150 ease-in-out border-b-2 border-b-transparent group-hover:border-[#FF9D01]"
                    >
                      {{ $t("installer.header.products.label") }}
                    </div>
                    <ChevronDownIcon
                      class="h-5 w-5 transition-transform text-[#666666]"
                    />
                    <div
                      v-if="showDropdown"
                      class="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                      @mouseover="onHandleHover('enter')"
                      @mouseleave="onHandleHover('leave')"
                    >
                      <div
                        class="max-w-7xl mx-auto px-4 sm:px-6 flex gap-12 py-12"
                      >
                        <div class="w-1/4 flex flex-col gap-2.5">
                          <p class="text-2xl base font-bold text-black">
                            {{ $t("installer.header.products.title") }}
                          </p>
                          <span class="text-xl font-medium text-gray-500">{{
                            $t("installer.header.products.description")
                          }}</span>
                          <span class="text-xl font-medium text-gray-500">{{
                            $t("installer.header.products.description-2")
                          }}</span>
                          <router-link
                            :to="{ name: 'InstallerCaseStudiesHomeOverview' }"
                            class="rounded text-center bg-gradient-to-r from-orange-500 to-yellow-400 px-3.5 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                          >
                            {{ $t("installer.header.products.button.title") }}
                          </router-link>
                          <p
                            class="text-base font-medium text-gray-500 text-center"
                          >
                            {{
                              $t("installer.header.products.button.sub-title")
                            }}
                          </p>

                          <!-- <LogoCloud
                            class="max-w-7xl mx-auto px-4 sm:px-6"
                            :align="'vertical'"
                          /> -->
                        </div>
                        <div class="w-3/4">
                          <div class="grid grid-cols-3 gap-y-12 gap-x-6">
                            <button
                              v-for="product in products"
                              :key="product"
                              type="button"
                              class="flex text-left items-start gap-4 cursor-pointer bg-orange-100/20 hover:bg-orange-200/20 p-4 border-2 focus:border-2 rounded-lg"
                              :class="{
                                'border-2 border-[#F76B1C]':
                                  selectedProduct === product,
                                'border-2 border-transparent':
                                  selectedProduct !== product,
                              }"
                              @click="selectProduct(product)"
                            >
                              <div
                                class="flex items-center justify-center gap-2 h-6 min-w-[24px] w-[24px] bg-[#FEF0E6] rounded-md"
                              >
                                <span
                                  class="h-3 w-3 bg-[#F76B1C] rounded-md"
                                ></span>
                              </div>
                              <div class="-mt-1">
                                <p
                                  class="text-lg font-bold text-[#666666] mb-1"
                                >
                                  {{ product["product-name"] }}
                                </p>
                                <p class="text-base font-medium text-gray-500">
                                  {{ product.description }}
                                </p>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div
                    class="flex items-center gap-2 group cursor-pointer"
                    @mouseover="onHandleHover('enter')"
                    @mouseleave="onHandleHover('leave')"
                  >
                    <div
                      :class="
                        dark && top
                          ? 'text-[#666666] group-hover:text-[#666666]'
                          : 'text-[#666666] group-hover:text-[#666666]'
                      "
                      class="flex items-center transition duration-150 ease-in-out border-b-2 border-b-transparent group-hover:border-[#FF9D01]"
                    >
                      {{ $t("installer.header.use-cases.label") }}
                    </div>
                    <ChevronDownIcon class="h-5 w-5 text-[#666666]" />
                    <div
                      v-if="showDropdown"
                      class="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                      @mouseover="onHandleHover('enter')"
                      @mouseleave="onHandleHover('leave')"
                    >
                      <div
                        class="max-w-7xl mx-auto px-4 sm:px-6 flex gap-12 py-12"
                      >
                        <div class="w-1/4 flex flex-col gap-2.5">
                          <p class="text-2xl base font-bold text-black">
                            {{ $t("installer.header.use-cases.title") }}
                          </p>
                          <span class="text-xl font-medium text-gray-500">{{
                            $t("installer.header.use-cases.description")
                          }}</span>
                          <span class="text-xl font-medium text-gray-500">{{
                            $t("installer.header.use-cases.description-2")
                          }}</span>
                          <router-link
                            :to="{ name: 'InstallerCaseStudiesHomeOverview' }"
                            class="rounded text-center bg-gradient-to-r from-orange-500 to-yellow-400 px-3.5 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                          >
                            {{ $t("installer.header.use-cases.button.title") }}
                          </router-link>
                          <p
                            class="text-base font-medium text-gray-500 text-center"
                          >
                            {{
                              $t("installer.header.use-cases.button.sub-title")
                            }}
                          </p>
                        </div>
                        <div class="w-3/4">
                          <div class="grid grid-cols-3 gap-y-12 gap-x-6">
                            <button
                              v-for="useCase in useCases"
                              :key="useCase"
                              type="button"
                              class="flex text-left items-start gap-4 cursor-pointer bg-orange-100/20 hover:bg-orange-200/20 p-4 border-2 focus:border-2 rounded-lg"
                              :class="{
                                'border-2 border-[#F76B1C]':
                                  selectedCases === useCase,
                                'border-2 border-transparent':
                                  selectedCases !== useCase,
                              }"
                              @click="selectCases(useCase)"
                            >
                              <div
                                class="flex items-center justify-center gap-2 h-6 min-w-[24px] w-[24px] bg-[#FEF0E6] rounded-md"
                              >
                                <span
                                  class="h-3 w-3 bg-[#F76B1C] rounded-md"
                                ></span>
                              </div>
                              <div class="-mt-1">
                                <p
                                  class="text-lg font-bold text-[#666666] mb-1"
                                >
                                  {{ useCase.name }}
                                </p>
                                <p class="text-base font-medium text-gray-500">
                                  {{ useCase.description }}
                                </p>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                      <!-- <LogoCloud class="max-w-7xl mx-auto px-4 sm:px-6" /> -->
                    </div>
                  </div>
                </li>
                <li>
                  <router-link
                    :to="{ name: 'HardwareMarketplaceAbout' }"
                    :class="
                      dark && top
                        ? 'text-[#666666] hover:text-[#666666]'
                        : 'text-[#666666] hover:text-[#666666]'
                    "
                    class="flex items-center transition duration-150 ease-in-out border-b-2 border-b-transparent hover:border-[#FF9D01]"
                  >
                    {{ $t("installer.header.buy-hardware") }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="{ name: 'InstallerFindNewClients' }"
                    :class="
                      dark && top
                        ? 'text-[#666666] hover:text-[#666666]'
                        : 'text-[#666666] hover:text-[#666666]'
                    "
                    class="flex items-center transition duration-150 ease-in-out border-b-2 border-b-transparent hover:border-[#FF9D01]"
                    >{{ $t("navigation.find-new-clients") }}</router-link
                  >
                </li>
              </div>
              <div
                v-if="!route.path.startsWith('/ambassadors')"
                class="flex items-center gap-5"
              >
                <div class="">
                  <li>
                    <button
                      :class="
                        dark && top
                          ? 'text-gray-200 hover:text-gray-400'
                          : 'text-gray-600 hover:text-gray-900'
                      "
                      class="rounded-full px-3 py-2 flex items-center transition duration-150 ease-in-out shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      @click="$emit('sign-in')"
                    >
                      <span>Connexion</span>
                    </button>
                  </li>
                </div>
                <div>
                  <li>
                    <button
                      class="rounded-full px-3.5 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 bg-gradient-to-r bg-gray-900 ml-3"
                      @click="$emit('sign-up')"
                    >
                      <span>Inscription</span>
                    </button>
                  </li>
                </div>
              </div>
            </ul>
          </nav>

          <!-- Mobile menu -->
          <div class="flex md:hidden">
            <!-- Hamburger button -->
            <button
              ref="hamburger"
              class="hamburger"
              :class="{ active: mobileNavOpen }"
              aria-controls="mobile-nav"
              :aria-expanded="mobileNavOpen"
              @click="mobileNavOpen = !mobileNavOpen"
            >
              <span class="sr-only">Menu</span>
              <svg
                class="w-6 h-6 fill-current text-gray-900"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect y="4" width="24" height="2" />
                <rect y="11" width="24" height="2" />
                <rect y="18" width="24" height="2" />
              </svg>
            </button>

            <!-- Mobile navigation -->
            <transition
              enter-active-class="transition ease-out duration-200 transform"
              enter-from-class="opacity-0 -translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-out duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <nav
                v-show="mobileNavOpen"
                id="mobile-nav"
                ref="mobileNav"
                class="absolute top-full h-screen pb-16 z-20 left-0 w-full overflow-scroll bg-white"
              >
                <ul class="px-5 py-8">
                  <!-- Dropdown -->
                  <li class="relative">
                    <!-- this button must take all the width with flex but don't-->
                    <button
                      class="flex cursor-pointer text-gray-600 hover:text-gray-900 py-2"
                      @click="showInstallers = !showInstallers"
                    >
                      {{ $t("navigation.installersDropdown") }}
                      <span class="ml-2">
                        <template v-if="showInstallers">
                          <ChevronUpIcon class="h-5" />
                        </template>
                        <template v-else>
                          <ChevronDownIcon class="h-5" />
                        </template>
                      </span>
                    </button>
                    <div v-if="showInstallers" class="mt-3 pb-3">
                      <a
                        v-for="item in solutions"
                        :key="item.name"
                        class="-m-3 flex items-start rounded-lg p-3 transition duration-150 ease-in-out hover:bg-gray-50 py-4"
                      >
                        <router-link :to="item.to">
                          <div class="ml-4">
                            <p class="text-gray-600">
                              {{ item.name }}
                            </p>
                          </div>
                        </router-link>
                      </a>
                    </div>
                  </li>
                  <!-- End of Dropdown -->

                  <li>
                    <router-link
                      to="/blog"
                      class="flex text-gray-600 hover:text-gray-900 py-2"
                      >{{ $t("navigation.blog") }}</router-link
                    >
                  </li>
                  <li>
                    <router-link
                      to="/about"
                      class="flex text-gray-600 hover:text-gray-900 py-2"
                      >{{ $t("navigation.about-us") }}</router-link
                    >
                  </li>
                  <template v-if="!route.path.startsWith('/ambassadors')">
                    <li>
                      <router-link
                        to="/signup"
                        class="flex text-gray-600 hover:text-gray-900 py-2"
                        >{{ $t("navigation.sign-up") }}</router-link
                      >
                    </li>

                    <li>
                      <router-link
                        to="/signin"
                        class="btn-sm text-gray-200 bg-gray-900 hover:bg-gray-800 w-full my-2"
                      >
                        <span>{{ $t("navigation.sign-in") }}</span>
                        <svg
                          class="w-3 h-3 fill-current text-gray-400 shrink-0 ml-2 -mr-1"
                          viewBox="0 0 12 12"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.707 5.293L7 .586 5.586 2l3 3H0v2h8.586l-3 3L7 11.414l4.707-4.707a1 1 0 000-1.414z"
                            fill="#999"
                            fill-rule="nonzero"
                          />
                        </svg>
                      </router-link>
                    </li>
                  </template>
                </ul>
              </nav>
            </transition>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>
